import { makeVar, useApolloClient, useReactiveVar } from "@apollo/client";
import Cookies from "js-cookie";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import screenfull from "screenfull";
import { useDebounce } from "use-debounce";

import { VideoResolution } from "../../__generated__/globalTypes";
import { HasbotContext } from "../../context/hasBot-context";
import { NotesContext } from "../../context/notes-context";
import { MODULE_QUERY, useAppState } from "../../hooks/useAppState";
import useCheckIfLibrary from "../../hooks/useCheckIfLibrary";
import { useDevice } from "../../hooks/useDevice";
import { ErrorSeverity, useErrorHandler } from "../../hooks/useErrorHandler";
import { useKeyPress } from "../../hooks/useKeyPress";
import { useMessages } from "../../hooks/useMessages";
import { usePersistedSettings } from "../../hooks/usePersistedSettings";
import { useProgress } from "../../hooks/useProgress";
import { useQCTicketModal } from "../../hooks/useQCTicketModal";
import { setVideoProgress } from "../../hooks/useVideoProgress";
import { createTelemetryEvent } from "../../telemetry/Telemetry";
import { sendClosedCaptionTelemetryEvent } from "../../util/captionLanguages";
import { fetchCloudflareTrace, getCloudflareBlock, getSupportReferenceId } from "../../util/cloudflareDiagnostics";
import {
  GEO_BLOCKING_ERROR_MESSAGE,
  saveProgressInterval,
  speedModifier,
  telemetryChannelName,
} from "../../util/constants";
import { getSecondsFromTimestamp, getTimestampFromSeconds } from "../../util/time";
import { useViewport, View } from "../Viewport/useViewport";
import { NetworkSpeedTest } from "./NetworkTesting";
import type { PlayerWithMediaState, VideoPlayerInstance } from "./playerTypes";
import { useCaptions } from "./useCaptions";
import { attemptPlay, shouldSuppressInitialAutoplay } from "./videoHelpers";

export const setIsPlaying = makeVar(false);
export const setTapToUnmute = makeVar(false);
export const setIsFullscreen = makeVar(false);
export const setIsFirstRender = makeVar(true);
export const featureName = "playback";
export const changeRateActionType = "changeRate";
export const videoLoadedActionType = "videoLoaded";
export const seekActionType = "seek";
export const muteToggledActionType = "muteToggled";
export const playActionType = "play";
export const pauseActionType = "pause";
export const enterFullscreenActionMetaType = "enter";
export const exitFullscreenActionMetaType = "exit";

export type ProgressState = {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
};

type QualityOrLanguageValue = VideoResolution | "auto" | "off" | "en" | undefined;
type CaptionTrack = { language: string; path?: string; source?: CaptionSource };
type HlsEventEmitter = {
  on?: (event: string, cb: () => void) => void;
  off?: (event: string, cb: () => void) => void;
};
type HlsSubtitleState = {
  subtitleTracks?: { lang?: string; language?: string }[];
  subtitleTrack?: number;
};
type CaptionSource = "hls" | "textTracks" | "manifest" | "none";
type CaptionPaths = { language: string; path: string }[] | Record<string, string> | null | undefined;
type CaptionPathResult = { url?: string; source?: string };

interface QualityOrLanguageChange {
  newValue: QualityOrLanguageValue;
  currentValue: QualityOrLanguageValue;
  setter: (value: QualityOrLanguageValue) => void;
}

const findCaptionPath = (captionPaths: CaptionPaths, language: string) => {
  if (!captionPaths) return undefined;

  if (Array.isArray(captionPaths)) {
    const captionPath =
      captionPaths.find(path => path.language === language) || captionPaths.find(path => path.language === "en");
    return captionPath?.path;
  }

  return captionPaths[language] || captionPaths["en"];
};

export const getCaptionPathForLanguageFromState = ({
  language,
  availableCaptionLanguages,
  availableCaptionLanguagesSlideId,
  slideId,
  videoURL,
  hlsManifestUrl,
  hlsCaptionAvailability,
  getHlsInstance,
  moduleCaptionPaths,
  moduleBaseUrl,
  slideNumber,
  slideCaptionsUrl,
  resolveCaptionTrackUrl,
}: {
  language: string;
  availableCaptionLanguages: CaptionTrack[];
  availableCaptionLanguagesSlideId: string | null;
  slideId?: string | null;
  videoURL?: string;
  hlsManifestUrl?: string | null;
  hlsCaptionAvailability: "pending" | "present" | "absent";
  getHlsInstance: () => unknown;
  moduleCaptionPaths: CaptionPaths;
  moduleBaseUrl?: string | null;
  slideNumber?: number | null;
  slideCaptionsUrl?: string | null;
  resolveCaptionTrackUrl: (path: string, baseUrl?: string) => string;
}): CaptionPathResult => {
  const normalized = language.toLowerCase();
  const currentTracks = availableCaptionLanguagesSlideId === slideId ? availableCaptionLanguages : [];
  const hasHlsTracks = currentTracks.some(track => track.source === "hls" || track.source === "manifest");
  const isHlsByUrl = videoURL?.endsWith(".m3u8") ?? false;
  const isHlsStream =
    Boolean(hlsManifestUrl) ||
    Boolean(getHlsInstance()) ||
    isHlsByUrl ||
    hasHlsTracks ||
    hlsCaptionAvailability !== "absent";
  const match =
    currentTracks.find(track => track.language?.toLowerCase() === normalized) ??
    currentTracks.find(track => track.language?.split("-")[0]?.toLowerCase() === normalized);

  if (match?.path) {
    return {
      url: resolveCaptionTrackUrl(match.path, hlsManifestUrl ?? videoURL),
      source: match.source ?? "hls",
    };
  }

  if (isHlsByUrl) {
    return {};
  }

  if (isHlsStream) {
    return {};
  }

  const captionPath = findCaptionPath(moduleCaptionPaths ?? [], language);
  if (captionPath && moduleBaseUrl && slideNumber) {
    const slideNumberString = slideNumber.toString().padStart(3, "0");
    return {
      url: `${moduleBaseUrl}${captionPath}/${slideNumberString}.vtt`,
      source: "module",
    };
  }

  if (slideCaptionsUrl) {
    return {
      url: slideCaptionsUrl,
      source: "slide",
    };
  }

  return {};
};

const isOneSecondVideo = (duration: number) => Math.floor(duration) === 1;

const getSafeResumeSeconds = (resumeSeconds: number, slideDurationSeconds?: number) => {
  if (!slideDurationSeconds || slideDurationSeconds <= 0) {
    return resumeSeconds;
  }

  // If resumeSeconds is in last 5% of slide, reset to 0 seconds, else restore to resumeSeconds
  const resumeResetThreshold = 0.95;
  return resumeSeconds >= slideDurationSeconds * resumeResetThreshold ? 0 : resumeSeconds;
};

const getBufferInfo = (videoElement: PlayerWithMediaState | null, duration: number, progress: ProgressState) => {
  if (!videoElement?.buffered || duration <= 0) {
    return {
      bufferedRanges: 0,
      loadedSeconds: "0.00",
      loadedPercentage: "0.0",
      totalBufferedTime: "0.00",
      currentChunkPercentage: "0.0",
    };
  }

  const buffered = videoElement.buffered;
  const ranges = [];
  const currentTime = progress.playedSeconds;

  // Collect all buffered ranges
  for (let i = 0; i < buffered.length; i++) {
    ranges.push({
      start: buffered.start(i),
      end: buffered.end(i),
    });
  }

  // Calculate total buffered time across all ranges
  const totalBufferedTime = ranges.reduce((sum, range) => sum + (range.end - range.start), 0);

  // Find the current chunk (range that contains current playback position)
  let currentChunkSize = 0;
  for (const range of ranges) {
    if (currentTime >= range.start && currentTime <= range.end) {
      currentChunkSize = range.end - range.start;
      break;
    }
  }

  return {
    bufferedRanges: buffered.length,
    loadedSeconds: progress.loadedSeconds.toFixed(2),
    loadedPercentage: (progress.loaded * 100).toFixed(1),
    totalBufferedTime: totalBufferedTime.toFixed(2),
    currentChunkPercentage: ((currentChunkSize / duration) * 100).toFixed(1),
  };
};

const formatBufferInfoForTelemetry = (duration: number, bufferInfo: ReturnType<typeof getBufferInfo>) => {
  const durationStr = getTimestampFromSeconds(duration);
  // Format: "duration: hh:mm:ss, total buffered: XXX.X% (this chunk: XXX.X%)"
  const bufferedStr =
    `total buffered: ${bufferInfo.loadedPercentage}% ` + `(this chunk: ${bufferInfo.currentChunkPercentage}%)`;
  return `duration: ${durationStr}, ${bufferedStr}`;
};

const getProgressTransitionMsFromSample = ({
  state,
  lastSample,
  playbackRate,
}: {
  state: ProgressState;
  lastSample: { played: number; playedSeconds: number; atMs: number } | null;
  playbackRate: number;
}) => {
  if (!lastSample) {
    return null;
  }

  const deltaSeconds = state.playedSeconds - lastSample.playedSeconds;
  if (deltaSeconds <= 0) {
    return null;
  }

  const inferredMs = Math.round((deltaSeconds / Math.max(playbackRate, 0.1)) * 1000);
  return Math.max(60, Math.min(500, inferredMs));
};

const shouldIgnoreProgressDuringSeek = ({
  slideId,
  isVideoEnded,
  seekInProgress,
  isSeeking,
  pendingSeekSeconds,
  state,
}: {
  slideId?: string | null;
  isVideoEnded: boolean;
  seekInProgress: boolean;
  isSeeking: boolean;
  pendingSeekSeconds: number | null;
  state: ProgressState;
}) => {
  if (!slideId || isVideoEnded || seekInProgress || isSeeking) {
    return true;
  }

  if (pendingSeekSeconds === null) {
    return false;
  }

  const seekToleranceSeconds = 0.75;
  return Math.abs(state.playedSeconds - pendingSeekSeconds) > seekToleranceSeconds;
};

let globalFocusPlayer: (() => void) | null = null;

export const isPlayablePlayer = (player: VideoPlayerInstance): player is { play: () => Promise<void> | void } => {
  return Boolean(player && "play" in player && typeof player.play === "function");
};

export const pausePlayer = (player: VideoPlayerInstance) => {
  if (player && "pause" in player && typeof player.pause === "function") {
    player.pause();
  }
};

export const runIfCurrentPlaybackRequest = (isCurrentRequest: () => boolean, action: () => void) => {
  if (isCurrentRequest()) {
    action();
  }
};

export default function useVideo(
  videoContainer: HTMLDivElement | null,
  videoPlayerWrapper: HTMLDivElement | null,
  videoPlayer: VideoPlayerInstance,
) {
  const {
    volume,
    setVolume,
    autoplay: isAutoplayEnabled,
    setAutoplay: setIsAutoplayEnabled,
    captions: isCaptions,
    setCaptions: setIsCaptions,
    playback,
    setPlayback,
    muted,
    setMuted,
    captionLanguage,
    setCaptionLanguage,
  } = usePersistedSettings();
  const PLAYBACK_RATES = [0.7, 1.0, 1.2, 1.5, 2.0];
  const [volumeState, setVolumeState] = useState(volume);
  const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });
  const isPlaying = useReactiveVar(setIsPlaying);
  const tapToUnmute = useReactiveVar(setTapToUnmute);
  const isFirstRender = useReactiveVar(setIsFirstRender);

  const [isSeeking, setIsSeeking] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const isFullscreen = useReactiveVar(setIsFullscreen);
  const [isPIPEnabled, setIsPIPEnabled] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(playback);
  const [duration, setDuration] = useState(0);
  const [forceShowVolumeSlider, setForceShowVolumeSlider] = useState(false);
  const [progress, setProgress] = useState<ProgressState>({
    played: 0,
    playedSeconds: 0,
    loaded: 0,
    loadedSeconds: 0,
  });
  const [progressTransitionMs, setProgressTransitionMs] = useState(100);
  const progressSeconds = useRef(0);
  const progressRef = useRef<ProgressState>(progress);
  const lastProgressSampleRef = useRef<{ played: number; playedSeconds: number; atMs: number } | null>(null);

  const [loaded, setLoaded] = useState(false);
  const [loadedVideoUrl, setLoadedVideoUrl] = useState<string | null>(null);
  const [buffering, setBuffering] = useState(false);
  const [ready, setReady] = useState(false);
  const [playbackStarting, setPlaybackStarting] = useState(false);
  const [originalDuration, setOriginalDuration] = useState(0);
  const [lastSavedProgress, setLastSavedProgress] = useState(0);

  const {
    slide,
    module,
    nextSlide,
    isLastSlide,
    course,
    appState: { courseId, instanceId, slideId, slideTime, videoPath, videoQuality },
    setSlide,
    setVideoPath,
    refreshModule,
  } = useAppState();

  const { changeView } = useViewport();
  const { isEditorFocused } = useContext(NotesContext);
  const { showDialog } = useContext(HasbotContext);
  const { modalOpen } = useQCTicketModal();
  const isLibrary = useCheckIfLibrary();
  const { modalOpen: qcTicketModalOpen } = useQCTicketModal();
  const { modalOpen: messagesModalOpen } = useMessages(isLibrary);
  const [videoTitle, setVideoTitle] = useState("");
  const [videoURL, setVideoURL] = useState<string>();
  const [captionsURL, setCaptionsURL] = useState<string | null>(null);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [hasNoAudio, setHasNoAudio] = useState(false);
  const [isGeoBlocked, setIsGeoBlocked] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [activeVideoQuality, setActiveVideoQuality] = useState(videoQuality);
  const [activeCaptionsLanguage, setActiveCaptionsLanguage] = useState(captionLanguage);
  const [legacyFormatOverride, setLegacyFormatOverride] = useState<"mp4" | "webm" | undefined>(undefined);
  const [availableCaptionLanguages, setAvailableCaptionLanguages] = useState<CaptionTrack[]>([]);
  const [availableCaptionLanguagesSlideId, setAvailableCaptionLanguagesSlideId] = useState<string | null>(null);
  const [hlsCaptionAvailability, setHlsCaptionAvailability] = useState<"pending" | "present" | "absent">("absent");
  const [hlsCaptionsChecked, setHlsCaptionsChecked] = useState(false);
  const [hlsManifestUrl, setHlsManifestUrl] = useState<string | null>(null);
  const [videoError, setVideoError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const lastCaptionSlideIdRef = useRef<string | null>(null);
  const activeSlideIdRef = useRef<string | null>(null);
  const activeVideoUrlRef = useRef<string | null>(null);
  const requestedSlideIdRef = useRef<string | null>(null);
  const availableCaptionLanguagesRef = useRef<CaptionTrack[]>([]);
  const availableCaptionLanguagesSlideIdRef = useRef<string | null>(null);

  useEffect(() => {
    activeSlideIdRef.current = slide?.id ?? null;
  }, [slide?.id]);

  useEffect(() => {
    requestedSlideIdRef.current = slideId ?? slide?.id ?? null;
  }, [slideId, slide?.id]);

  useEffect(() => {
    setLegacyFormatOverride(undefined);
    setVideoError(false);
    setRetryToken(0);
    setVideoURL(undefined);
    setLoadedVideoUrl(null);
  }, [slide?.id]);

  // Resolve URL synchronously; fallback to module.slides when slide is undefined (e.g. between setSlide's
  // two state updates) to prevent black-box flash
  const slideOrFromModule = slide ?? (slideId ? module?.slides?.find(s => s.id === slideId) : undefined);
  const resolvedVideoURL =
    slideOrFromModule?.cloudflareVideo?.signedUrl ??
    (module?.baseUrl && videoPath && slideOrFromModule?.number != null ? `${module?.baseUrl}${videoPath}` : null);
  const effectiveVideoURL = resolvedVideoURL ?? videoURL;
  const isCurrentSourceLoaded = Boolean(effectiveVideoURL) && loadedVideoUrl === effectiveVideoURL;

  useEffect(() => {
    activeVideoUrlRef.current = effectiveVideoURL ?? null;
  }, [effectiveVideoURL]);

  useEffect(() => {
    availableCaptionLanguagesRef.current = availableCaptionLanguages;
  }, [availableCaptionLanguages]);

  useEffect(() => {
    availableCaptionLanguagesSlideIdRef.current = availableCaptionLanguagesSlideId;
  }, [availableCaptionLanguagesSlideId]);
  const [autoplayNext, setAutoplayNext] = useState(false);
  const [playAfterSeek, setPlayAfterSeek] = useState(false);
  const [playerHovered, setPlayerHovered] = useState(false);
  const videoEndedRef = useRef(false);
  const refreshSeekSecondsRef = useRef<number | null>(null);
  const refreshResumeSlideIdRef = useRef<string | null>(null);
  const refreshSuppressUntilRef = useRef<number | null>(null);
  const refreshResumeWasPlayingRef = useRef(false);
  const refreshTimeoutRef = useRef<number | null>(null);
  const refreshAbortRef = useRef<AbortController | null>(null);
  const pendingAutoplayRef = useRef(false);
  const suppressInitialAutoplayRef = useRef(false);
  const initialAutoplaySlideIdRef = useRef<string | null>(slide?.id ?? null);
  const pendingSeekSecondsRef = useRef<number | null>(null);
  const pendingSeekProgressRef = useRef<ProgressState | null>(null);
  const seekInProgressRef = useRef(false);
  const wasPlayingBeforeSeekRef = useRef(false);
  const playbackRequestRef = useRef(0);
  const hasStartedPlaybackRef = useRef(false);
  const [disableSliderTransition, setDisableSliderTransition] = useState(false);
  const { text: captionsText } = useCaptions(
    isCaptions,
    captionsURL ?? undefined,
    progress.playedSeconds,
    hasNoAudio,
    slide?.id,
  );
  const [loading] = useDebounce(buffering || !loaded, 300);

  useEffect(() => {
    setVolumeState(volume);
  }, [volume]);

  const { isTouchDevice } = useDevice();

  const { getAndMaybeTestNetworkSpeed } = NetworkSpeedTest();

  const playerRef = useRef(videoPlayer);
  const client = useApolloClient();
  const lastCaptionUrlSourceRef = useRef<string | null>(null);

  const invalidatePlaybackSession = useCallback(() => {
    playbackRequestRef.current += 1;
    activeSlideIdRef.current = null;
    activeVideoUrlRef.current = null;
    pendingAutoplayRef.current = false;
    setPlaybackStarting(false);
    setReady(false);
    setLoaded(false);
    setLoadedVideoUrl(null);
  }, []);

  const getVideoElement = useCallback((): HTMLVideoElement | null => {
    const player = playerRef.current;
    if (player && "textTracks" in player) {
      return player as HTMLVideoElement;
    }
    if (player && "getInternalPlayer" in player) {
      const internal = (player as { getInternalPlayer?: () => unknown }).getInternalPlayer?.();
      if (internal && typeof internal === "object" && "textTracks" in internal) {
        return internal as HTMLVideoElement;
      }
    }
    return null;
  }, []);

  const getHlsInstance = useCallback((): unknown => {
    const player = playerRef.current;
    if (!player || !("getInternalPlayer" in player)) {
      return null;
    }
    return (player as { getInternalPlayer?: (key?: string) => unknown }).getInternalPlayer?.("hls") ?? null;
  }, []);

  const getCaptionTracksFromPlayer = useCallback((): { tracks: CaptionTrack[]; source: CaptionSource } => {
    // If we have a manifest URL, we don’t need to check the player for caption tracks,
    // because the manifest URL is the source of truth for the caption tracks
    if (hlsManifestUrl) {
      return { tracks: [], source: "none" };
    }

    const hls = getHlsInstance() as HlsSubtitleState | null;
    const hlsTracks = hls?.subtitleTracks ?? [];
    const hlsLanguages = hlsTracks
      .map(track => ({
        language: track.lang ?? track.language,
        path: (track as { url?: string }).url,
      }))
      .filter((track): track is { language: string; path: string | undefined } => Boolean(track.language))
      .map(track => ({ ...track, source: "hls" as const }));

    // If HLS tracks, return them. HLS streams are served as a single .m3u8 file, not as a playlist of .vtt files
    if (hlsLanguages.length > 0) {
      return { tracks: hlsLanguages, source: "hls" };
    }

    const videoElement = getVideoElement();
    const textTracks = videoElement?.textTracks ? Array.from(videoElement.textTracks) : [];
    const textTrackLanguages = textTracks
      .map(track => track.language)
      .filter((language): language is string => Boolean(language))
      .map(language => ({ language, source: "textTracks" as const }));

    // If text tracks, return them. This is for videos served as a single .vtt file, not as a playlist of .vtt files
    if (textTrackLanguages.length > 0) {
      return { tracks: textTrackLanguages, source: "textTracks" };
    }

    // If no caption tracks, return empty array. This is for videos that have no captions, or a playlist of .vtt files
    return { tracks: [], source: "none" };
  }, [getHlsInstance, getVideoElement, hlsManifestUrl]);

  const updateAvailableCaptionLanguages = useCallback((): boolean => {
    const currentSlideId = activeSlideIdRef.current;
    // Slide ID hasn’t changed, and we already fetched caption tracks for this slide
    // This is when we have as a single .vtt file, not as a playlist of .vtt files
    if (
      availableCaptionLanguagesSlideIdRef.current === currentSlideId &&
      availableCaptionLanguagesRef.current.length > 0
    ) {
      return true;
    }

    const { tracks } = getCaptionTracksFromPlayer();
    if (tracks.length > 0) {
      const seen = new Set<string>();
      const uniqueTracks = tracks.filter(track => {
        if (seen.has(track.language)) {
          return false;
        }
        seen.add(track.language);
        return true;
      });
      setAvailableCaptionLanguages(uniqueTracks);
      setAvailableCaptionLanguagesSlideId(currentSlideId);
      return true;
    }
    return false;
  }, [getCaptionTracksFromPlayer]);

  const parseManifestCaptionTracks = useCallback((manifestText: string, manifestUrl: string): CaptionTrack[] => {
    const lines = manifestText.split("\n");
    const subtitleLines = lines.filter(line => line.startsWith("#EXT-X-MEDIA") && line.includes("TYPE=SUBTITLES"));

    const stripQuotes = (value: string) => {
      if (value.startsWith('"') && value.endsWith('"')) {
        return value.slice(1, -1);
      }
      return value;
    };

    const parseAttributes = (line: string) => {
      const attrs: Record<string, string> = {};
      const raw = line.substring(line.indexOf(":") + 1);
      let current = "";
      let inQuotes = false;

      for (const char of raw) {
        if (char === '"') {
          inQuotes = !inQuotes;
          current += char;
        } else if (char === "," && !inQuotes) {
          const [key, value] = current.split("=");
          if (key && value) {
            attrs[key.trim()] = stripQuotes(value.trim());
          }
          current = "";
        } else {
          current += char;
        }
      }

      const [key, value] = current.split("=");
      if (key && value) {
        attrs[key.trim()] = stripQuotes(value.trim());
      }

      return attrs;
    };

    return subtitleLines
      .map(line => parseAttributes(line))
      .map(attrs => ({
        language: attrs.LANGUAGE,
        path: attrs.URI ? new URL(attrs.URI, manifestUrl).toString() : undefined,
      }))
      .filter((track): track is { language: string; path: string | undefined } => Boolean(track.language))
      .map(track => ({ ...track, source: "manifest" as const }));
  }, []);

  const fetchManifestCaptionTracks = useCallback(
    async (manifestUrl: string, signal: AbortSignal): Promise<CaptionTrack[]> => {
      try {
        const response = await fetch(manifestUrl, { signal });
        if (!response.ok) {
          return [];
        }
        const text = await response.text();
        return parseManifestCaptionTracks(text, manifestUrl);
      } catch {
        return [];
      }
    },
    [parseManifestCaptionTracks],
  );

  const refreshCaptionTracksForSlide = useCallback(async () => {
    const currentSlideId = activeSlideIdRef.current;
    const expectedVideoUrl =
      slide?.cloudflareVideo?.signedUrl ?? (module?.baseUrl && videoPath ? `${module.baseUrl}${videoPath}` : null);
    if (expectedVideoUrl && videoURL !== expectedVideoUrl) {
      return;
    }
    const slideChanged = Boolean(slide?.id) && lastCaptionSlideIdRef.current !== slide?.id;
    if (slideChanged) {
      lastCaptionSlideIdRef.current = slide?.id ?? null;
      setAvailableCaptionLanguages([]);
      setAvailableCaptionLanguagesSlideId(null);
      setHlsCaptionsChecked(false);
      setHlsCaptionAvailability("pending");
    }

    const hadTracks = slideChanged ? false : updateAvailableCaptionLanguages();
    if (hadTracks) {
      setHlsCaptionAvailability("present");
      setHlsCaptionsChecked(true);
      return;
    }

    const manifestUrl = hlsManifestUrl ?? (videoURL?.endsWith(".m3u8") ? videoURL : null);
    if (!manifestUrl) {
      setHlsCaptionAvailability("absent");
      setHlsCaptionsChecked(true);
      return;
    }

    const controller = new AbortController();
    const tracks = await fetchManifestCaptionTracks(manifestUrl, controller.signal);
    if (currentSlideId && currentSlideId !== activeSlideIdRef.current) {
      return;
    }
    if (tracks.length > 0) {
      setAvailableCaptionLanguages(tracks);
      setAvailableCaptionLanguagesSlideId(currentSlideId);
      setHlsCaptionAvailability("present");
    } else {
      setHlsCaptionAvailability("absent");
    }
    setHlsCaptionsChecked(true);
  }, [
    fetchManifestCaptionTracks,
    updateAvailableCaptionLanguages,
    videoURL,
    hlsManifestUrl,
    slide?.id,
    slide?.cloudflareVideo?.signedUrl,
    module?.baseUrl,
    videoPath,
  ]);

  const resolveCaptionTrackUrl = useCallback((path: string, baseUrl?: string): string => {
    try {
      return new URL(path, baseUrl).toString();
    } catch {
      return path;
    }
  }, []);

  const appendCaptionCacheBust = useCallback((url: string, slideId?: string | null): string => {
    if (!slideId) {
      return url;
    }
    try {
      const parsed = new URL(url);
      parsed.searchParams.set("slide", slideId);
      return parsed.toString();
    } catch {
      return url;
    }
  }, []);

  const getCaptionPathForLanguage = useCallback(
    (language: string): CaptionPathResult =>
      getCaptionPathForLanguageFromState({
        language,
        availableCaptionLanguages,
        availableCaptionLanguagesSlideId,
        slideId: slide?.id,
        videoURL,
        hlsManifestUrl,
        hlsCaptionAvailability,
        getHlsInstance,
        moduleCaptionPaths: module?.captionPaths,
        moduleBaseUrl: module?.baseUrl,
        slideNumber: slide?.number,
        slideCaptionsUrl: slide?.captionsUrl,
        resolveCaptionTrackUrl,
      }),
    [
      availableCaptionLanguages,
      availableCaptionLanguagesSlideId,
      hlsCaptionAvailability,
      getHlsInstance,
      module?.captionPaths,
      module?.baseUrl,
      slide?.number,
      slide?.id,
      slide?.captionsUrl,
      resolveCaptionTrackUrl,
      videoURL,
      hlsManifestUrl,
    ],
  );

  const focusPlayer = useCallback(() => {
    const controlContainer = document.getElementById("video-control-container");

    if (controlContainer) {
      if (!controlContainer.hasAttribute("tabindex")) {
        controlContainer.setAttribute("tabindex", "-1");
      }

      setTimeout(() => {
        controlContainer.focus();
      }, 100);
    }
  }, []);

  globalFocusPlayer = focusPlayer;

  useEffect(() => {
    if (isFirstRender) {
      if (isAutoplayEnabled) {
        setIsPlaying(false);
        suppressInitialAutoplayRef.current = true;
      }

      setIsFirstRender(false);
    }
  }, [isFirstRender, isAutoplayEnabled, setMuted]);

  useEffect(() => {
    playbackRequestRef.current += 1;
    playerRef.current = videoPlayer;
  }, [videoPlayer]);

  const skipTimerRef = useRef<NodeJS.Timeout>(null);

  const { saveProgress, syncLocalSlideProgress } = useProgress();
  const { i18n } = useTranslation();

  const { reportError } = useErrorHandler();

  const preventStandardHotKeyActions = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  type HandlerProps = {
    event: React.KeyboardEvent;
    isDocumentFocused: boolean;
    isPlayerActive: boolean;
  };

  const shortcutHandlers = [
    {
      key: " ",
      handler: ({ event, isDocumentFocused, isPlayerActive }: HandlerProps) => {
        const isVideoContainerFocused = document.activeElement === document.getElementById("video-control-container");

        if (isPlayerActive) {
          preventStandardHotKeyActions(event);
          handleClickPlayPause();
        }

        if (isDocumentFocused || isVideoContainerFocused) {
          showControls();
          focusPlayer();
        }
      },
    },
    {
      key: "p",
      handler: ({ event }: HandlerProps) => {
        preventStandardHotKeyActions(event);
        handleClickPreviousVideo();
      },
    },
    {
      key: "n",
      handler: ({ event }: HandlerProps) => {
        preventStandardHotKeyActions(event);
        handleClickNextVideo();
      },
    },
    {
      key: "j",
      handler: ({ event }: HandlerProps) => {
        preventStandardHotKeyActions(event);
        handleClickReplay();
      },
    },
    {
      key: "<",
      handler: ({ event }: HandlerProps) => {
        preventStandardHotKeyActions(event);

        const playbackRateIndex = PLAYBACK_RATES.findIndex(val => val === playback) - 1;
        const newPlaybackRate = PLAYBACK_RATES[playbackRateIndex < 0 ? 0 : playbackRateIndex];

        setPlaybackRate(isOneSecondVideo(originalDuration) ? newPlaybackRate * speedModifier : newPlaybackRate);
        setPlayback(newPlaybackRate);
      },
    },
    {
      key: ">",
      handler: ({ event }: HandlerProps) => {
        preventStandardHotKeyActions(event);

        const playbackRateIndex = PLAYBACK_RATES.findIndex(val => val === playback) + 1;
        const newPlaybackRate =
          PLAYBACK_RATES[playbackRateIndex > PLAYBACK_RATES.length - 1 ? PLAYBACK_RATES.length - 1 : playbackRateIndex];

        setPlaybackRate(isOneSecondVideo(originalDuration) ? newPlaybackRate * speedModifier : newPlaybackRate);
        setPlayback(newPlaybackRate);
      },
    },
    {
      key: "ArrowUp",
      handler: ({ event, isPlayerActive }: HandlerProps) => {
        if (isPlayerActive) {
          preventStandardHotKeyActions(event);

          const newVolume = volumeState + 0.1;
          const nextVolume = Math.min(1, newVolume);
          const rounded = Math.round(nextVolume * 100) / 100;
          setVolumeState(rounded);
          setVolume(rounded);
          showVolumeSlider();
          showControls();
        }
      },
    },
    {
      key: "ArrowRight",
      handler: () => skip(10),
    },
    {
      key: "ArrowLeft",
      handler: () => skip(-10),
    },
    {
      key: "ArrowDown",
      handler: ({ event, isPlayerActive }: HandlerProps) => {
        if (isPlayerActive) {
          preventStandardHotKeyActions(event);

          const newVolume = volumeState - 0.1;
          const nextVolume = Math.max(0, newVolume);
          const rounded = Math.round(nextVolume * 100) / 100;
          setVolumeState(rounded);
          setVolume(rounded);
          showVolumeSlider();
          showControls();
        }
      },
    },
    {
      key: "m",
      handler: ({ event }: HandlerProps) => {
        preventStandardHotKeyActions(event);

        handleClickMuteVolume();
        showControls();
      },
    },
    {
      key: "f",
      handler: ({ event }: HandlerProps) => {
        preventStandardHotKeyActions(event);

        handleClickFullscreen();
      },
    },
    {
      key: "a",
      handler: ({ event }: HandlerProps) => {
        preventStandardHotKeyActions(event);

        handleClickAutoplay(!isAutoplayEnabled);
        showControls();
      },
    },
    {
      key: "c",
      handler: ({ event }: HandlerProps) => {
        preventStandardHotKeyActions(event);

        const newLanguage = !isCaptions ? "en" : "off";
        setCaptionLanguage(newLanguage);
        handleClickCaptions(!isCaptions);
        showControls();

        sendClosedCaptionTelemetryEvent(newLanguage);
      },
    },
  ];

  const onKeyPress = (event: React.KeyboardEvent, playerActive?: boolean) => {
    const { key } = event;
    const isModeDepressed = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    const isButtonFocused = document.activeElement?.tagName === "BUTTON";
    const controlContainer = document.getElementById("video-control-container");
    const isControlButtonFocused = Boolean(
      isButtonFocused && controlContainer?.contains(document.activeElement as Node),
    );
    const found = shortcutHandlers.find(handler => handler.key === key);
    const isPlayerActive = playerActive || videoContainer?.contains(document.activeElement as Node) || false;
    const isDocumentFocused = document.activeElement === document.body;
    const isInputFocused = document.activeElement?.tagName === "INPUT";

    if (
      found &&
      !isInputFocused &&
      !(isButtonFocused && !isControlButtonFocused) &&
      !isEditorFocused &&
      !isModeDepressed &&
      !showDialog &&
      !qcTicketModalOpen &&
      !messagesModalOpen &&
      !modalOpen
    ) {
      found.handler({ event, isDocumentFocused, isPlayerActive });
    }
  };

  useKeyPress(
    shortcutHandlers.map(({ key }) => key),
    onKeyPress,
  );

  const save = useCallback(
    async (moduleRefresh: boolean, seconds = progress.playedSeconds) => {
      setLastSavedProgress(seconds);
      await saveProgress(moduleRefresh, seconds);
    },
    [progress.playedSeconds, saveProgress],
  );

  useEffect(() => {
    progressSeconds.current = progress.playedSeconds;
    progressRef.current = progress;
    if (progress.playedSeconds - lastSavedProgress > saveProgressInterval) save(true);
  }, [progress, progress.playedSeconds, lastSavedProgress, save]);

  const setTime = useCallback(
    (seconds: number) => {
      if (!videoPlayer) return;

      setProgress(prevState => {
        const newProgress = {
          ...prevState,
          played: duration > 0 ? seconds / duration : 0,
          playedSeconds: seconds,
        };
        setVideoProgress(newProgress);
        return newProgress;
      });

      if (videoPlayer && "currentTime" in videoPlayer) {
        videoPlayer.currentTime = seconds;
      }
    },
    [videoPlayer, duration],
  );

  const setPlaybackState = useCallback(async (shouldPlay: boolean) => {
    const player = playerRef.current;
    if (!player) return;
    const requestId = ++playbackRequestRef.current;
    const requestSlideId = activeSlideIdRef.current;
    const isCurrentRequest = () =>
      requestId === playbackRequestRef.current && requestSlideId === activeSlideIdRef.current;

    if (shouldPlay) {
      if (!isPlayablePlayer(player)) {
        return;
      }

      runIfCurrentPlaybackRequest(isCurrentRequest, () => {
        setPlaybackStarting(true);
        setIsPlaying(true);
      });

      const didPlay = await attemptPlay({
        player,
        onNotAllowed: () => {
          runIfCurrentPlaybackRequest(isCurrentRequest, () => {
            setIsPlaying(false);
          });
        },
        onError: error => {
          console.warn("Failed to play video:", error);
        },
      });

      if (!didPlay) {
        runIfCurrentPlaybackRequest(isCurrentRequest, () => setPlaybackStarting(false));
        return;
      }
      if (!isCurrentRequest()) {
        return;
      }
      setPlaybackStarting(false);
      setIsPlaying(true);
      return;
    }

    setPlaybackStarting(false);
    setIsPlaying(false);
    try {
      pausePlayer(player);
    } catch (error) {
      console.warn("Failed to pause video:", error);
    }
  }, []);

  const getAdjacentSlideId = useCallback(
    (step: -1 | 1): string | null => {
      const slides = module?.slides ?? [];
      if (!slides.length) return null;

      const baseSlideId = requestedSlideIdRef.current ?? slideId ?? slide?.id ?? null;
      if (!baseSlideId) return null;

      const currentIndex = slides.findIndex(currentSlide => currentSlide.id === baseSlideId);
      if (currentIndex < 0) return null;

      const nextIndex = currentIndex + step;
      if (nextIndex < 0 || nextIndex >= slides.length) return null;

      return slides[nextIndex]?.id ?? null;
    },
    [module?.slides, slideId, slide?.id],
  );

  const navigateToSlideId = useCallback(
    async (targetSlideId: string) => {
      requestedSlideIdRef.current = targetSlideId;
      setIsPlaying(false);
      invalidatePlaybackSession();
      pendingAutoplayRef.current = isAutoplayEnabled && !isFirstRender;
      save(true);
      await setSlide(targetSlideId);
    },
    [invalidatePlaybackSession, isAutoplayEnabled, isFirstRender, save, setSlide],
  );

  useEffect(() => {
    playbackRequestRef.current += 1;
    setReady(false);
  }, [slideId]);

  useEffect(() => {
    if (!ready) {
      const shouldAutoplay = (autoplayNext || isAutoplayEnabled) && !isFirstRender;
      pendingAutoplayRef.current = shouldAutoplay;
      setAutoplayNext(false);
      setIsPlaying(false);

      setReady(true);
    }
  }, [ready, autoplayNext, isAutoplayEnabled, isFirstRender]);

  const updatePlayerProgress = useCallback(() => {
    if (hasStartedPlaybackRef.current && (progressRef.current?.playedSeconds ?? 0) > 0) {
      return;
    }
    const resumeSeconds = slideTime ? getSecondsFromTimestamp(slideTime) : 0;
    const slideDurationSeconds = slide?.duration ? getSecondsFromTimestamp(slide.duration) : duration;
    setTime(getSafeResumeSeconds(resumeSeconds, slideDurationSeconds));
  }, [slideTime, slide?.duration, duration, setTime]);

  useEffect(() => {
    setLoaded(false);
  }, [slideId]);

  useEffect(() => {
    setAvailableCaptionLanguages([]);
    if (!videoURL) return;

    const hls = getHlsInstance() as HlsEventEmitter | null;
    const videoElement = getVideoElement();
    const textTracks = videoElement?.textTracks;
    const controller = new AbortController();
    const manifestUrl =
      hlsManifestUrl ?? (videoURL.endsWith(".m3u8") ? videoURL : ((hls as { url?: string } | null)?.url ?? null));
    const isHlsSource = Boolean(manifestUrl) || Boolean(hls);

    setHlsCaptionAvailability(isHlsSource ? "pending" : "absent");
    setHlsCaptionsChecked(!isHlsSource);

    const handleTracksUpdate = () => {
      const currentSlideId = activeSlideIdRef.current;
      const nextManifestUrl = (hls as { url?: string } | null)?.url ?? null;
      if (nextManifestUrl && manifestUrl && nextManifestUrl !== manifestUrl) {
        setHlsManifestUrl(nextManifestUrl);
        return;
      }
      if (nextManifestUrl && nextManifestUrl !== hlsManifestUrl) {
        setHlsManifestUrl(nextManifestUrl);
      }
      const hadTracks = updateAvailableCaptionLanguages();
      if (hadTracks) {
        setHlsCaptionAvailability("present");
        setHlsCaptionsChecked(true);
      }
      if (!hadTracks && manifestUrl) {
        fetchManifestCaptionTracks(manifestUrl, controller.signal)
          .then(tracks => {
            if (currentSlideId && currentSlideId !== activeSlideIdRef.current) {
              return;
            }
            if (tracks.length > 0) {
              setAvailableCaptionLanguages(tracks);
              setHlsCaptionAvailability("present");
              setHlsCaptionsChecked(true);
            } else {
              setHlsCaptionAvailability("absent");
              setHlsCaptionsChecked(true);
            }
          })
          .catch(() => {});
      }
      if (!hadTracks && !manifestUrl && isHlsSource) {
        setHlsCaptionAvailability("absent");
        setHlsCaptionsChecked(true);
      }
    };

    if (hls?.on && hls?.off) {
      hls.on("hlsManifestParsed", handleTracksUpdate);
      hls.on("hlsSubtitleTracksUpdated", handleTracksUpdate);
    }

    if (textTracks?.addEventListener) {
      textTracks.addEventListener("addtrack", handleTracksUpdate);
      textTracks.addEventListener("removetrack", handleTracksUpdate);
    }

    handleTracksUpdate();

    return () => {
      controller.abort();
      if (hls?.off) {
        hls.off("hlsManifestParsed", handleTracksUpdate);
        hls.off("hlsSubtitleTracksUpdated", handleTracksUpdate);
      }
      if (textTracks?.removeEventListener) {
        textTracks.removeEventListener("addtrack", handleTracksUpdate);
        textTracks.removeEventListener("removetrack", handleTracksUpdate);
      }
    };
  }, [
    videoURL,
    slide?.id,
    hlsManifestUrl,
    getHlsInstance,
    getVideoElement,
    updateAvailableCaptionLanguages,
    fetchManifestCaptionTracks,
  ]);

  useEffect(() => {
    if (!hlsManifestUrl && !videoURL?.endsWith(".m3u8")) {
      return;
    }

    if (captionsURL && lastCaptionUrlSourceRef.current === "slide") {
      setCaptionsURL(null);
      lastCaptionUrlSourceRef.current = "hls-block";
    }
  }, [videoURL, captionsURL, hlsManifestUrl]);

  useEffect(() => {
    const signedUrl = slide?.cloudflareVideo?.signedUrl;
    const hasLegacyUrl = Boolean(module?.baseUrl && videoPath && slide?.number);
    const nextVideoUrl = signedUrl ?? (hasLegacyUrl ? `${module?.baseUrl ?? ""}${videoPath}` : null);

    if (!nextVideoUrl) {
      return;
    }

    if (slide?.fullNumber || slide?.name) {
      setVideoTitle(`${slide?.fullNumber}: ${slide?.name}`);
    }
    setVideoURL(nextVideoUrl);
    if (nextVideoUrl.endsWith(".m3u8")) {
      setHlsManifestUrl(nextVideoUrl);
    }
    if (refreshResumeSlideIdRef.current === slide?.id) {
      if (refreshSuppressUntilRef.current && Date.now() < refreshSuppressUntilRef.current) {
        return;
      }
      refreshSuppressUntilRef.current = null;
    }
    updatePlayerProgress();
  }, [
    module?.baseUrl,
    slide?.fullNumber,
    slide?.name,
    slide?.cloudflareVideo?.signedUrl,
    slide?.id,
    videoPath,
    slide?.number,
    updatePlayerProgress,
    setTime,
  ]);

  useEffect(() => {
    refreshSeekSecondsRef.current = null;
    refreshResumeSlideIdRef.current = null;
    refreshSuppressUntilRef.current = null;
    refreshResumeWasPlayingRef.current = false;
    hasStartedPlaybackRef.current = false;
    setCaptionsURL(null);
    lastCaptionUrlSourceRef.current = null;
    setHlsCaptionAvailability("absent");
    setHlsCaptionsChecked(false);
    setHlsManifestUrl(null);
    setAvailableCaptionLanguages([]);
    setAvailableCaptionLanguagesSlideId(null);
  }, [slide?.id]);

  useEffect(() => {
    const trainingId = course?.trainingId;
    const slideNumber = slide?.number;
    const expiresAt = slide?.cloudflareVideo?.expiresAt;
    const signedUrl = slide?.cloudflareVideo?.signedUrl;

    if (!trainingId || !slideNumber || !expiresAt || !signedUrl) return;

    const expiresMs = Date.parse(expiresAt);
    if (Number.isNaN(expiresMs)) return;

    // Refesh 1 min before signedUrl expiry
    const refreshAtMs = expiresMs - 60_000;
    const delayMs = Math.max(0, refreshAtMs - Date.now());

    const controller = new AbortController();
    refreshAbortRef.current = controller;

    refreshTimeoutRef.current = globalThis.setTimeout(async () => {
      try {
        const playerTime =
          playerRef.current && "currentTime" in playerRef.current ? playerRef.current.currentTime : null;
        refreshSeekSecondsRef.current = playerTime ?? progressRef.current?.playedSeconds ?? 0;
        refreshResumeSlideIdRef.current = slide?.id ?? null;
        // Suppress refresh for 15 seconds to avoid refreshing while is changing
        refreshSuppressUntilRef.current = Date.now() + 15_000;
        const wasPlaying = isPlayingRef.current;
        refreshResumeWasPlayingRef.current = wasPlaying;
        // Pause till video source change is complete
        if (wasPlaying) {
          await setPlaybackState(false);
        }
        if (!module?.id) return;
        // Refesh module to get latest values
        await client.query({
          query: MODULE_QUERY,
          variables: { moduleId: module.id, instanceId: instanceId ?? undefined, courseId: courseId ?? undefined },
          fetchPolicy: "network-only",
        });
        // Sets module and slide in app state to latest values
        await refreshModule();
      } catch (error) {
        if ((error as DOMException).name === "AbortError") return;
        console.warn("Cloudflare URL refresh failed:", error);
      }
    }, delayMs);

    return () => {
      if (refreshTimeoutRef.current !== null) {
        globalThis.clearTimeout?.(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      if (refreshAbortRef.current) {
        refreshAbortRef.current.abort();
        refreshAbortRef.current = null;
      }
    };
  }, [
    course?.trainingId,
    slide?.number,
    slide?.cloudflareVideo?.expiresAt,
    slide?.cloudflareVideo?.signedUrl,
    slide?.id,
    module?.id,
    courseId,
    instanceId,
    client,
    refreshModule,
    setPlaybackState,
  ]);

  const handleQualityOrLanguageChange = useCallback(
    async ({ newValue, currentValue, setter }: QualityOrLanguageChange) => {
      if (newValue !== currentValue && slideId && setSlide) {
        const seconds = progressRef.current?.playedSeconds ?? 0;
        const wasPlaying = isPlaying;
        await setPlaybackState(false);
        setter(newValue);
        await setSlide(slideId, getTimestampFromSeconds(seconds));
        setTime(seconds);
        if (wasPlaying) {
          await setPlaybackState(true);
        }
      }
    },
    [slideId, setSlide, setTime, isPlaying, setPlaybackState],
  );

  // Single effect to handle both caption and quality changes
  useEffect(() => {
    handleQualityOrLanguageChange({
      newValue: captionLanguage,
      currentValue: activeCaptionsLanguage,
      setter: setActiveCaptionsLanguage,
    });
  }, [activeCaptionsLanguage, captionLanguage, handleQualityOrLanguageChange]);

  useEffect(() => {
    const hls = getHlsInstance() as HlsSubtitleState | null;
    if (hls?.subtitleTracks && typeof hls.subtitleTrack === "number") {
      // Disable the current subtitle track
      hls.subtitleTrack = -1;
    }

    const videoElement = getVideoElement();
    if (videoElement?.textTracks) {
      Array.from(videoElement.textTracks).forEach(track => {
        // Disable the current text track
        track.mode = "disabled";
      });
    }
  }, [captionLanguage, getHlsInstance, getVideoElement]);

  useEffect(() => {
    if (captionLanguage === "off") {
      setCaptionsURL(null);
      lastCaptionUrlSourceRef.current = "off";
      return;
    }

    if (!videoURL) {
      return;
    }

    const currentTracks = availableCaptionLanguagesSlideId === slide?.id ? availableCaptionLanguages : [];
    const hlsInstance = getHlsInstance() as HlsSubtitleState | null;
    const hasHlsTracks = currentTracks.some(track => track.source === "hls" || track.source === "manifest");
    const isHlsSource =
      Boolean(hlsManifestUrl) ||
      (videoURL?.endsWith(".m3u8") ?? false) ||
      Boolean(hlsInstance) ||
      hasHlsTracks ||
      hlsCaptionAvailability !== "absent";
    if (isHlsSource && !hlsCaptionsChecked) {
      setCaptionsURL(null);
      lastCaptionUrlSourceRef.current = "hls-checking";
      return;
    }
    if (isHlsSource) {
      const normalized = captionLanguage.toLowerCase();
      const hlsMatch =
        currentTracks.find(track => track.language?.toLowerCase() === normalized) ??
        currentTracks.find(track => track.language?.split("-")[0]?.toLowerCase() === normalized);

      if (hlsMatch?.path) {
        const resolvedUrl = resolveCaptionTrackUrl(hlsMatch.path, hlsManifestUrl ?? videoURL);
        const url = appendCaptionCacheBust(resolvedUrl, slide?.id);
        setCaptionsURL(url);
        lastCaptionSlideIdRef.current = slide?.id ?? null;
        lastCaptionUrlSourceRef.current = hlsMatch.source ?? "hls";
      } else {
        setCaptionsURL(null);
        lastCaptionUrlSourceRef.current = "hls-pending";
      }
      return;
    }

    const { url, source } = getCaptionPathForLanguage(captionLanguage);
    const nextUrl = url ?? null;
    setCaptionsURL(nextUrl);
    lastCaptionUrlSourceRef.current = source ?? "none";
  }, [
    captionLanguage,
    getCaptionPathForLanguage,
    availableCaptionLanguagesSlideId,
    availableCaptionLanguages,
    hlsCaptionAvailability,
    hlsCaptionsChecked,
    getHlsInstance,
    appendCaptionCacheBust,
    resolveCaptionTrackUrl,
    videoURL,
    slide?.id,
    hlsManifestUrl,
  ]);

  useEffect(() => {
    if (!isCaptions || !videoURL) {
      return;
    }
    if (lastCaptionSlideIdRef.current === slide?.id) {
      return;
    }
    void refreshCaptionTracksForSlide();
  }, [slide?.id, videoURL, isCaptions, hlsManifestUrl, refreshCaptionTracksForSlide]);

  useEffect(() => {
    handleQualityOrLanguageChange({
      newValue: videoQuality,
      currentValue: activeVideoQuality,
      setter: setActiveVideoQuality,
    });
  }, [activeVideoQuality, videoQuality, handleQualityOrLanguageChange]);

  useEffect(() => {
    if (!module?.cookies) return;
    for (const cookie of module.cookies) {
      const { key, value, path, domain, expires, secure } = cookie;
      Cookies.set(key, value, { path, domain, expires: new Date(expires), secure });
    }
  }, [module?.cookies]);

  // Preflight check for Cloudflare Stream signed URLs
  useEffect(() => {
    // Only check Cloudflare Stream URLs
    if (!videoURL?.includes("cloudflarestream.com")) {
      setIsGeoBlocked(false);
      setIsCheckingAccess(false);
      return;
    }

    const controller = new AbortController();
    setIsCheckingAccess(true);

    // Use GET with Range header to fetch only first byte - better CORS compatibility
    fetch(videoURL, {
      method: "GET",
      headers: {
        Range: "bytes=0-0",
      },
      signal: controller.signal,
    })
      .then(async res => {
        if (res.status === 401 || res.status === 403) {
          setIsGeoBlocked(true);
          const timestamp = Date.now();
          const cloudflareBlock = getCloudflareBlock(res);
          const cloudflareTrace = await fetchCloudflareTrace(controller.signal);
          const supportReferenceId = getSupportReferenceId(cloudflareBlock);
          const errorReferenceId = supportReferenceId ?? timestamp;
          reportError(ErrorSeverity.MEDIUM, `This video is ${GEO_BLOCKING_ERROR_MESSAGE} (${errorReferenceId})`, {
            geoIPBlocked: true,
            timestamp,
            url: videoURL,
            cloudflareBlock,
            cloudflareTrace,
          });
        } else if (res.status === 404) {
          setVideoError(true);
          reportError(ErrorSeverity.LOW, "Error loading video", {
            videoURL,
            status: res.status,
            expiresAt: slide?.cloudflareVideo?.expiresAt,
          });
        } else {
          setIsGeoBlocked(false);
        }
      })
      .catch(err => {
        if (err.name !== "AbortError") {
          console.error("Preflight check failed:", err);
          // If fetch fails entirely (network/CORS), assume video might be blocked
          // Let the video player try to load - it may have better error handling
          setIsGeoBlocked(false);
        }
      })
      .finally(() => {
        setIsCheckingAccess(false);
      });

    return () => controller.abort();
  }, [videoURL, reportError, slide?.cloudflareVideo?.expiresAt]);

  const lastSlideId = useRef<string | null>(null);

  useEffect(() => {
    if (!slideId || lastSlideId.current === slideId) return;

    lastSlideId.current = slideId;

    // After 5 seconds (to give head start to current slide video
    // loading), get (and maybe test) network speed test
    setTimeout(() => {
      getAndMaybeTestNetworkSpeed().catch(error => {
        console.warn("Network speed test failed:", error);
      });
    }, 5000);
  }, [slideId, getAndMaybeTestNetworkSpeed]);

  useEffect(() => {
    if (!loaded || !duration || !slideId || slideId === slide?.id) return;
    setSlide(slideId);

    if (refreshResumeSlideIdRef.current === slide?.id) {
      if (refreshSuppressUntilRef.current && Date.now() < refreshSuppressUntilRef.current) {
        return;
      }
      refreshSuppressUntilRef.current = null;
    }
    setTimeout(() => updatePlayerProgress());
  }, [loaded, duration, slideId, slide?.id, setSlide, updatePlayerProgress]);

  useEffect(() => {
    setVideoDimensions({
      width: videoPlayerWrapper?.clientWidth || 0,
      height: videoPlayerWrapper?.clientHeight || 0,
    });
  }, [videoPlayerWrapper]);

  const handleVideoProgress = (state: ProgressState) => {
    if (
      shouldIgnoreProgressDuringSeek({
        slideId,
        isVideoEnded: videoEndedRef.current,
        seekInProgress: seekInProgressRef.current,
        isSeeking,
        pendingSeekSeconds: pendingSeekSecondsRef.current,
        state,
      })
    ) {
      return;
    }

    if (pendingSeekSecondsRef.current !== null) {
      pendingSeekSecondsRef.current = null;
    }

    setProgress(state);
    setVideoProgress(state);

    if (isPlaying) {
      const nowMs = performance.now();
      const nextTransitionMs = getProgressTransitionMsFromSample({
        state,
        lastSample: lastProgressSampleRef.current,
        playbackRate,
      });

      if (nextTransitionMs !== null) {
        setProgressTransitionMs(nextTransitionMs);
      }

      lastProgressSampleRef.current = { played: state.played, playedSeconds: state.playedSeconds, atMs: nowMs };
      syncLocalSlideProgress(slideId as string, state.playedSeconds);
    } else {
      lastProgressSampleRef.current = null;
    }
  };

  const handleVideoDuration = (duration: number) => {
    setOriginalDuration(duration);

    let videoDuration = duration;
    const customPlayback = Number((playback * speedModifier).toFixed(2));

    if (isOneSecondVideo(duration)) {
      videoDuration = 10;
      setPlaybackRate(customPlayback);
      setPlayback(playback);
      if (hasNoAudio) {
        setHasNoAudio(false);
      }
      setHasNoAudio(true);
    } else {
      setPlayback(playback);
      setPlaybackRate(playback);
      setHasNoAudio(false);
    }

    setDuration(videoDuration);
  };

  const handleVideoReady = () => {
    const isStaleReadyEvent =
      (slide?.id ?? null) !== activeSlideIdRef.current || (effectiveVideoURL ?? null) !== activeVideoUrlRef.current;

    if (isStaleReadyEvent) {
      return;
    }

    let didResume = false;
    if (refreshSeekSecondsRef.current !== null && refreshResumeSlideIdRef.current === slide?.id) {
      const resumeSeconds = refreshSeekSecondsRef.current;
      refreshSeekSecondsRef.current = null;
      setTime(resumeSeconds);
      didResume = true;
      if (refreshResumeWasPlayingRef.current) {
        refreshResumeWasPlayingRef.current = false;
        void setPlaybackState(true);
      }
    }
    if (!didResume && videoPlayer && !isSkipping && progress.playedSeconds >= duration) {
      setTime(0);
    }
    if (pendingAutoplayRef.current) {
      pendingAutoplayRef.current = false;
      if (
        !shouldSuppressInitialAutoplay({
          suppressInitialAutoplay: suppressInitialAutoplayRef.current,
          slideId: slide?.id,
          initialAutoplaySlideId: initialAutoplaySlideIdRef.current,
        })
      ) {
        void setPlaybackState(true);
      }
      suppressInitialAutoplayRef.current = false;
    }

    setVideoError(false);
    setLoaded(true);
    setLoadedVideoUrl(effectiveVideoURL ?? null);
    if (!loaded) save(true);

    // If HLS instance, set the manifest URL
    const hls = getHlsInstance() as { url?: string } | null;
    const nextManifestUrl = hls?.url ?? null;
    if (nextManifestUrl && nextManifestUrl !== hlsManifestUrl) {
      setHlsManifestUrl(nextManifestUrl);
    }

    void refreshCaptionTracksForSlide();

    // Get enhanced buffer information
    const bufferInfo = getBufferInfo(videoPlayer, duration, progress);
    const actionMeta = formatBufferInfoForTelemetry(duration, bufferInfo);

    createTelemetryEvent({
      feature: featureName,
      action: "video data loaded",
      action_meta: actionMeta,
    }).fire(telemetryChannelName);
  };

  // Helper function to save progress with retry logic
  const saveProgressWithRetry = useCallback(async (): Promise<boolean> => {
    try {
      await save(true);
      return true;
    } catch (error) {
      console.warn("Failed to save progress on video end, will try again. Error:", error);

      // First save attempt failed - retry once
      try {
        await save(true);
        return true;
      } catch (retryError) {
        console.error("Failed to save progress on video end. Error:", retryError);

        // Report error after both attempts failed
        reportError(ErrorSeverity.MEDIUM, "Failed to save progress when video ended", {
          error,
          retryError,
          slideId,
          isAutoplayEnabled,
          isLastSlide,
        });

        // Both save attempts failed - pause video, user will need to reload page
        await setPlaybackState(false);

        // Clean up state
        videoEndedRef.current = false;
        if (!isAutoplayEnabled) {
          setDisableSliderTransition(false);
        }

        return false;
      }
    }
  }, [save, setPlaybackState, isAutoplayEnabled, reportError, slideId, isLastSlide]);

  const handleVideoEnded = async () => {
    // Block progress updates immediately
    videoEndedRef.current = true;

    if (!isAutoplayEnabled) {
      // Disable slider transition so it snaps to 0 instantly (no animation)
      setDisableSliderTransition(true);

      // Reset video element and progress to 0 RIGHT NOW (before async save)
      await setPlaybackState(false);
      if (videoPlayer && "currentTime" in videoPlayer) {
        videoPlayer.currentTime = 0;
      }
      const resetProgress = {
        played: 0,
        playedSeconds: 0,
        loaded: progress.loaded,
        loadedSeconds: progress.loadedSeconds,
      };
      setProgress(resetProgress);
      setVideoProgress(resetProgress);
    }

    // Attempt to save progress with one retry on failure
    const saveSucceeded = await saveProgressWithRetry();

    // If save failed after retry, don't continue with normal flow (e.g. next slide)
    if (!saveSucceeded) {
      return;
    }

    if (isLastSlide) {
      changeView(View.Confidence);
    }

    if (!isAutoplayEnabled) {
      // Keep blocking for 200ms, then re-enable transitions
      setTimeout(() => {
        videoEndedRef.current = false;
        setDisableSliderTransition(false);
      }, 200);

      setAutoplayNext(true);
      setIsPlaying(false);
      return;
    }

    // Autoplay enabled: reset videoEndedRef so next video can update progress
    videoEndedRef.current = false;
    // Pause current playback before changing the source to avoid play/pause race.
    setIsPlaying(false);
    invalidatePlaybackSession();
    nextSlide();
  };

  const handleClickPlayPause = async () => {
    if (!playerRef.current) {
      if (!isPlaying && effectiveVideoURL) {
        pendingAutoplayRef.current = true;
        setIsPlaying(true);
        setControlsVisible(true);
      }
      return;
    }

    const playerReadyState =
      playerRef.current && "readyState" in playerRef.current ? Number(playerRef.current.readyState) : 0;
    const shouldDeferPlay =
      !isPlaying && !isCurrentSourceLoaded && (!Number.isFinite(playerReadyState) || playerReadyState < 2);

    if (shouldDeferPlay) {
      pendingAutoplayRef.current = true;
      setIsPlaying(true);
      setControlsVisible(true);
      return;
    }

    await setPlaybackState(!isPlaying);
    // save, but don't refresh module; avoids playhead jumping forwards slightly
    if (isPlaying) await save(false);
    setControlsVisible(true);

    const action = !isPlaying ? playActionType : pauseActionType;
    createTelemetryEvent({
      feature: featureName,
      action,
      action_meta:
        `position: ${getTimestampFromSeconds(progress.playedSeconds)} ` + `of ${getTimestampFromSeconds(duration)}`,
    }).fire(telemetryChannelName);

    if (duration > 0 && progress.playedSeconds >= duration) {
      return nextSlide();
    }
  };

  const handleClickPreviousVideo = async () => {
    const targetSlideId = getAdjacentSlideId(-1);
    if (!targetSlideId) return;
    await navigateToSlideId(targetSlideId);
  };

  const handleClickNextVideo = async () => {
    const targetSlideId = getAdjacentSlideId(1);
    if (targetSlideId) {
      await navigateToSlideId(targetSlideId);
      return;
    }

    if (isLastSlide) {
      changeView(View.Confidence);
    }
  };

  const handleChangeView = () => {
    save(true);
    changeView(View.Slides);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChangeProgressBar = (_: any, value: number | number[]) => {
    const newPlayed = value as number;
    const newProgress = { ...progressRef.current, played: newPlayed, playedSeconds: newPlayed * duration };
    pendingSeekProgressRef.current = newProgress;

    // Disable transition for instant seeking
    setDisableSliderTransition(true);

    // Update progress immediately for responsive dragging
    setProgress(newProgress);
    setVideoProgress(newProgress);
  };

  const handlePlaybackStarted = useCallback(() => {
    hasStartedPlaybackRef.current = true;
    setBuffering(false);
  }, []);

  const commitProgressBarSeek = useCallback(() => {
    if (!seekInProgressRef.current && !pendingSeekProgressRef.current) {
      return;
    }

    seekInProgressRef.current = false;
    setIsSeeking(false);
    const progressAtRelease = pendingSeekProgressRef.current ?? progressRef.current;
    const newTime = progressAtRelease.played * duration;

    pendingSeekSecondsRef.current = newTime;
    pendingSeekProgressRef.current = null;
    lastProgressSampleRef.current = null;
    setTime(newTime);

    // Update video progress with the new position
    const newProgress = {
      ...progressAtRelease,
      playedSeconds: newTime,
    };
    setVideoProgress(newProgress);

    if (wasPlayingBeforeSeekRef.current) {
      void setPlaybackState(true);
    }

    // Re-enable transitions after seek completes (MUI default is ~200ms)
    setTimeout(() => {
      setDisableSliderTransition(false);
    }, 250);

    // Fire telemetry event for seek action
    createTelemetryEvent({
      feature: featureName,
      action: seekActionType,
      action_meta: `position: ${getTimestampFromSeconds(newTime)} of ${getTimestampFromSeconds(duration)}`,
    }).fire(telemetryChannelName);
  }, [duration, setPlaybackState, setTime]);

  const handleMouseOutProgressBar = () => {
    // Keep the preview position visible while dragging, but do not commit the seek until mouseup.
  };

  const handleMouseUpProgressBar = () => {
    commitProgressBarSeek();
  };

  const handleMouseDownProgressBar = () => {
    wasPlayingBeforeSeekRef.current = isPlaying;
    seekInProgressRef.current = true;
    pendingSeekProgressRef.current = null;
    lastProgressSampleRef.current = null;
    setIsSeeking(true);
    document.addEventListener("mouseup", commitProgressBarSeek, { once: true });
  };

  const handleChangeVolumeSlider = (event: Event, value: number | number[]) => {
    setVolumeState(value as number);
  };

  const handleCommitVolumeSlider = (event: Event, value: number | number[]) => {
    const rounded = Math.round((value as number) * 100) / 100;
    setVolume(rounded);
    focusPlayer();
  };

  const handleClickAutoplay = (value: boolean) => {
    setIsAutoplayEnabled(value);
  };

  const handleClickReplay = () => {
    const replayTime = 0;
    setTime(replayTime);
    save(true, replayTime);
  };

  const handleClickPIP = () => {
    setIsPIPEnabled(!isPIPEnabled);
  };

  const handleClickPlaybackRate = (value: number) => {
    const playback = value;
    const customPlayback = isOneSecondVideo(originalDuration) ? playback * speedModifier : playback;

    setPlaybackRate(customPlayback);
    setPlayback(playback);

    createTelemetryEvent({
      feature: featureName,
      action: "change rate",
      action_meta: `playback rate: ${playback}`,
    }).fire(telemetryChannelName);
  };

  const muteActionRef = useRef(false);

  const handleClickMuteVolume = () => {
    const isMuted = volumeState === 0;
    const newMutedState = isMuted ? true : !muted;

    // Mark that this is a user action
    muteActionRef.current = true;

    // Update muted state first
    setMuted(newMutedState);
    setTapToUnmute(false);

    if (muted && volumeState === 0) {
      const rounded = Math.round(0.3 * 100) / 100;
      setVolumeState(rounded);
      setVolume(rounded);
    }
  };

  // Fire telemetry event when muted state changes due to user action
  useEffect(() => {
    if (muteActionRef.current) {
      const action_meta = muted ? "muted" : "unmuted";
      muteActionRef.current = false; // Reset the flag
      createTelemetryEvent({
        feature: featureName,
        action: "mute toggled",
        action_meta,
      }).fire(telemetryChannelName);
    }
  }, [muted]);

  const handleClickCaptions = (value: boolean) => {
    setIsCaptions(value);
  };

  const handleClickFullscreen = async () => {
    if (!videoContainer || !screenfull.isEnabled) {
      return;
    }

    const newFullscreenState = !isFullscreen;

    setIsFullscreen(newFullscreenState);

    const action_meta = newFullscreenState ? enterFullscreenActionMetaType : exitFullscreenActionMetaType;

    try {
      await screenfull.toggle(videoContainer);
    } catch (error) {
      setIsFullscreen(isFullscreen);
      if ((error as { name?: string } | null)?.name !== "NotAllowedError") {
        console.warn("Failed to toggle fullscreen:", error);
      }
      return;
    }

    // Small delay to let reactive variable update
    setTimeout(() => {
      createTelemetryEvent({
        feature: featureName,
        action: "fullscreen toggled",
        action_meta,
      }).fire(telemetryChannelName);
    }, 0);
  };

  const handleError = (error: unknown, data?: unknown, hlsInstance?: unknown, hlsGlobal?: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { code, message } = (error as any)?.target?.error ?? {};

    // If attempt to get `.webm` file fails, try `.mp4` as fallback
    if (code === 4 && videoURL?.endsWith(".webm") && slide?.number && legacyFormatOverride !== "mp4") {
      setLegacyFormatOverride("mp4");
      setVideoPath(slide.number, "mp4");
      return;
    }

    setVideoError(true);
    reportError(ErrorSeverity.LOW, "Error loading video", {
      error,
      data,
      hlsInstance,
      hlsGlobal,
      mediaError: code ? { code, message } : undefined,
    });
  };

  const retryVideo = useCallback(() => {
    setVideoError(false);
    setLoaded(false);
    setBuffering(true);
    setRetryToken(prev => prev + 1);
  }, []);

  const timeoutRef: { current: NodeJS.Timeout | null } = useRef(null);
  const doubleClickTimeoutRef: { current: NodeJS.Timeout | null } = useRef(null);

  const playerHoveredRef = useRef(playerHovered);
  playerHoveredRef.current = playerHovered;

  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;

  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  const volumeSliderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showVolumeSlider = useCallback(() => {
    if (volumeSliderTimeoutRef.current) {
      globalThis.clearTimeout?.(volumeSliderTimeoutRef.current);
    }
    setForceShowVolumeSlider(true);
    volumeSliderTimeoutRef.current = globalThis.setTimeout(() => {
      setForceShowVolumeSlider(false);
    }, 1500);
  }, []);

  const clearTimeoutRef = () => {
    globalThis.clearTimeout?.(timeoutRef.current as NodeJS.Timeout);
  };
  const clearDoubleClickTimeoutRef = () => {
    globalThis.clearTimeout?.(doubleClickTimeoutRef.current as NodeJS.Timeout);
  };

  const showControls = useCallback(() => {
    clearTimeoutRef();

    setControlsVisible(true);

    if (playAfterSeek) {
      setIsPlaying(false);
    }

    timeoutRef.current = setTimeout(() => {
      if (isPlayingRef.current && !loadingRef.current) {
        setControlsVisible(false);
      }

      if (playAfterSeek) {
        void setPlaybackState(true);
        setPlayAfterSeek(false);
      }
    }, 3000);
  }, [setControlsVisible, playAfterSeek, setPlaybackState]);

  useEffect(() => {
    if (module?.id && slide?.id && slide.number) {
      setVideoPath(slide.number, legacyFormatOverride);
    }
  }, [module?.id, slide?.id, slide?.number, setVideoPath, legacyFormatOverride]);

  useEffect(() => {
    if (slide?.id) {
      // Show controls after slide navigation to restore cursor visibility and provide user feedback
      showControls();
    }
  }, [slide?.id, showControls]);

  // Show controls when video becomes ready so user sees the play button (and its attention-grabbing pop)
  const prevLoadingForReadyRef = useRef(loading);
  useEffect(() => {
    if (prevLoadingForReadyRef.current && !loading && !videoError) {
      setControlsVisible(true);
    }
    prevLoadingForReadyRef.current = loading;
  }, [loading, videoError, setControlsVisible]);

  const handleClickVideoContainer = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const containerClick = target.id === "controls-center";
    if (isTouchDevice || !containerClick) return;

    if (doubleClickTimeoutRef.current) {
      clearDoubleClickTimeoutRef();
      doubleClickTimeoutRef.current = null;
      handleClickFullscreen();
    } else {
      doubleClickTimeoutRef.current = setTimeout(() => {
        doubleClickTimeoutRef.current = null;
        handleClickPlayPause();
      }, 250);
    }
  };

  const skip = (amount: number) => {
    if (skipTimerRef.current) {
      globalThis.clearTimeout?.(skipTimerRef.current);
    }

    // Ensure skip doesn't inherit touch-seek resume behavior.
    setPlayAfterSeek(false);
    showControls();
    setIsSkipping(true);
    setIsSeeking(false);

    const targetSeconds = Math.max(0, Math.min(duration, progress.playedSeconds + amount));
    const nextProgress = {
      ...progress,
      played: duration > 0 ? targetSeconds / duration : 0,
      playedSeconds: targetSeconds,
    };

    // Disable transition for instant skip update
    setDisableSliderTransition(true);
    setProgress(nextProgress);
    setVideoProgress(nextProgress);
    if (videoPlayer && "currentTime" in videoPlayer) {
      videoPlayer.currentTime = targetSeconds;
    }

    const viaLabel = amount < 0 ? "-10 secs" : "+10 secs";
    createTelemetryEvent({
      feature: featureName,
      action: seekActionType,
      action_meta: `position: ${getTimestampFromSeconds(targetSeconds)} of ${getTimestampFromSeconds(
        duration,
      )}, via ${viaLabel}`,
    }).fire(telemetryChannelName);

    // Re-enable transitions after skip completes (MUI default is ~200ms)
    setTimeout(() => {
      setDisableSliderTransition(false);
    }, 250);
    skipTimerRef.current = setTimeout(() => setIsSkipping(false), 1000);
  };

  // Register screenfull listener once to avoid multiple events
  useEffect(() => {
    if (!screenfull.isEnabled) return;

    const handleFullscreenChange = () => {
      if (!screenfull.isFullscreen && isFullscreen) {
        setIsFullscreen(false);

        // Small delay to let reactive variable update
        setTimeout(() => {
          createTelemetryEvent({
            feature: featureName,
            action: "fullscreen toggled",
            action_meta: exitFullscreenActionMetaType,
          }).fire(telemetryChannelName);
        }, 0);
      }
    };

    screenfull.on("change", handleFullscreenChange);
    return () => screenfull.off("change", handleFullscreenChange);
  }, [isFullscreen]);

  // Effect to change i18n language based on captionLanguage
  useEffect(() => {
    if (captionLanguage && captionLanguage !== "off") {
      i18n.changeLanguage(captionLanguage);
    } else if (captionLanguage === "off") {
      // Revert to fallback language (e.g., English) if captions are off
      // This ensures UI elements like 'noAudioSlide' use the fallback language.
      i18n.changeLanguage("en");
    }
  }, [captionLanguage, i18n]);

  return {
    videoDimensions,
    videoTitle,
    videoURL,
    effectiveVideoURL,
    handleVideoProgress,
    handleVideoDuration,
    handleVideoEnded,
    handleClickPreviousVideo,
    handleClickNextVideo,
    isAutoplayEnabled,
    handleClickAutoplay,
    isPlaying,
    handleClickPlayPause,
    isSeeking,
    progress,
    handleMouseUpProgressBar,
    handleMouseDownProgressBar,
    handleMouseOutProgressBar,
    handleChangeProgressBar,
    handleClickReplay,
    isFullscreen,
    handleClickFullscreen,
    isPIPEnabled,
    handleClickPIP,
    playbackRate,
    handleClickPlaybackRate,
    volume: volumeState,
    handleClickMuteVolume,
    handleChangeVolumeSlider,
    handleCommitVolumeSlider,
    duration,
    handleVideoReady,
    handleError,
    captionsURL,
    captionsEnabled: isCaptions,
    handleClickCaptions,
    handleChangeView,
    controlsVisible,
    setControlsVisible,
    showControls,
    setIsFullscreen,
    PLAYBACK_RATES,
    availableCaptionLanguages,
    hasNoAudio,
    setHasNoAudio,
    muted,
    handleClickVideoContainer,
    hasCustomDuration: duration !== originalDuration,
    onKeyPress,
    setDuration,
    updatePlayerProgress,
    setTime,
    setMuted,
    playAfterSeek,
    setPlayAfterSeek,
    skip,
    isSkipping,
    tapToUnmute,
    setTapToUnmute,
    playerHovered,
    setPlayerHovered,
    handlePlaybackStarted,
    isCurrentSourceLoaded,
    isPlaybackStarting: playbackStarting,
    loading,
    setBuffering,
    isFirstRender,
    setReady,
    captionsText,
    captionLanguage,
    setCaptionLanguage,
    focusPlayer,
    forceShowVolumeSlider,
    progressTransitionMs,
    disableSliderTransition,
    videoError,
    retryToken,
    retryVideo,
    videoEndedRef,
    isGeoBlocked,
    isCheckingAccess,
  };
}

export function setFocusToPlayer() {
  if (globalFocusPlayer) {
    globalFocusPlayer();
  }
}
